import {
  FormsViewData,
  useAssessmentForm,
  useAssessmentFromId,
  useMyAssessments,
  useSignOnComplete,
} from '@hb/react-web'
import { getPregnancyProgress } from '@hb/react-web/src/utils/assessments/progress'
import { AssessmentFormKey, AssessmentStepKey, getQuestionnaireSummaryComplete } from '@hb/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'

export const useFormsViewData = (): FormsViewData => {
  const { assessments, loading: assessmentsLoading, user: userData, userId } = useMyAssessments()
  const { user } = userData ?? {}
  const [selected, setSelected] = useState<{ formId: AssessmentFormKey; stageId: string } | null>(
    null,
  )
  const { assessmentId: _assessmentId } = useParams<{ assessmentId: string }>()
  const index = useMemo(() => {
    const keys = Object.keys(assessments ?? {})
    if (_assessmentId) {
      const idx = keys.findIndex(id => _assessmentId === id)
      return idx
    }
    return 0
  }, [assessments, _assessmentId])

  const assessmentId = useMemo(
    () => _assessmentId ?? (assessments && Object.keys(assessments)[0]) ?? undefined,
    [assessments, _assessmentId],
  )

  const assessmentData = useAssessmentFromId(false, assessmentId)
  // const { data: assessment } = useDocument<Assessment>('assessments', assessmentId)
  const { assessmentData: assessment, populated } = assessmentData ?? {}
  const [step, selectStep] = useState<AssessmentStepKey>('questions')

  const mountedAssessment = useRef<{
    createdOn: number | null
    sentOn: number | null
  } | null>(
    assessment
      ? { createdOn: assessment.createdOn ?? null, sentOn: assessment.sentOn ?? null }
      : null,
  )
  useEffect(() => {
    if (mountedAssessment.current?.createdOn === assessment?.createdOn) {
      if (!mountedAssessment.current?.sentOn && assessment?.sentOn) {
        selectStep('view')
      }
      return
    }
    if (assessment?.resultsViewedOn && !!assessment?.scheduledCall?.callDate) selectStep('signOn')
    else if (assessment?.results) selectStep('view')
    else {
      selectStep('questions')
    }
    mountedAssessment.current = assessment
      ? { createdOn: assessment.createdOn, sentOn: assessment.sentOn ?? null }
      : null
  }, [assessment])

  const { form } = useAssessmentForm(false, populated, selected?.formId)

  const signOnData = useSignOnComplete(user, assessmentData.populated)

  const questionnaireSummaryComplete = useMemo(
    () => getQuestionnaireSummaryComplete(assessmentData?.assessmentData?.data ?? {}),
    [assessmentData],
  )

  const progress = useMemo(
    () => getPregnancyProgress(assessmentData.populated, signOnData),
    [assessmentData.populated, signOnData],
  )

  return {
    step,
    assessmentId,
    assessment: assessmentData,
    assessments,
    selectStep,
    selected,
    populatedUser: userData,
    signOnData,
    progress,
    formData: assessment?.data ?? {},
    formCorrections: assessment?.corrections ?? {},
    assessmentIndex: index,
    open: (formId, stageId) => {
      setSelected({ formId, stageId })
    },
    close: () => {
      setSelected(null)
    },
    assessmentsLoading,
    userId,
    form,
    questionnaireSummaryComplete,
  }
}
